import {
  Box,
  Typography,
  Grid2 as Grid,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material'
import {
  PictureAsPdf,
  Comment,
  ArrowBack,
  FeedbackOutlined
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { EXAM_TYPE_MAP } from '../constants'

dayjs.extend(utc)
dayjs.extend(timezone)
import ButtonBase from 'design-system/button'
import { TranscriptionInput } from 'design-system/input'
import { CXRAY_LIST_CONFIG, ECG_LIST_CONFIG } from '../common/configs'
import ECGDiagnostics from './components/ECG/diagnostics'
import ECGExam from './components/ECG/exam'
import XRayDiagnostics from './components/XRay/diagnostics'
import XRayExam from './components/XRay/exam'
import { isApPaSubtype } from './components/XRay/constants/organs'
import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import {
  ECGAnnotationsProvider,
  useECGAnnotations
} from './components/ECG/context/ECGAnnotationsContext'
import { ECGAnnotationsSummary } from './components/ECG/components/ECGAnnotationsSummary'
import {
  XRayAnnotationsProvider,
  useXRayAnnotations
} from './components/XRay/context/XRayAnnotationsContext'
import { OrganSelectionProvider } from './components/XRay/context/OrganSelectionContext'
import { XRayAnnotationsSummary } from './components/XRay/components/XRayAnnotationsSummary'
import { PrintableReport } from './components/PrintableReport'
import { useGeneratePDF } from 'hooks/useGeneratePDF'
import {
  DiagnosisAgreementProvider,
  useDiagnosisAgreement
} from './context/DiagnosisAgreementContext'
import {
  ExamSubtype,
  IExamReportResponse,
  IExamResponse,
  IFeedbackPayload
} from 'types/exam/types'
import {
  getExamDetail,
  updateDiagnosisStatus,
  createFeedback,
  uploadReviewPDF,
  getExamReports
} from 'services/exam/examService'
import DiagnosticDetailSkeleton from './components/DiagnosticDetailSkeleton'
import { useToast } from 'services/context/toast'
import Storage from 'storage/auth'
import { FeedbackDialog } from './components/FeedbackDialog'
import { AccordionBase } from 'components/Accordion'
import { Reference } from 'components/Reference'
import { getEstablishmentById } from 'services/establishment/establishmentService'
import analytics from 'services/analytics'

/**
 * Helper function to restore filters from sessionStorage and build URL params.
 * A chave de storage é específica de cada listagem (Raio-X ou ECG).
 */
const getFiltersUrlParams = (storageKey: string): string => {
  const lastFiltersStr = sessionStorage.getItem(storageKey)
  if (!lastFiltersStr) return ''

  try {
    const lastFilters = JSON.parse(lastFiltersStr)
    const params = new URLSearchParams()

    if (
      lastFilters.diagnosticTypes?.length > 0 &&
      lastFilters.diagnosticTypes[0] !== 'none'
    ) {
      params.append('diagnosticTypes', lastFilters.diagnosticTypes.join(','))
    }
    if (lastFilters.examDateStart) {
      params.append('examDateStart', lastFilters.examDateStart)
    }
    if (lastFilters.examDateEnd) {
      params.append('examDateEnd', lastFilters.examDateEnd)
    }
    if (lastFilters.patientId?.trim()) {
      params.append('patientId', lastFilters.patientId)
    }
    if (lastFilters.examId?.trim()) {
      params.append('examId', lastFilters.examId)
    }
    if (lastFilters.diagnosticId?.trim()) {
      params.append('diagnosticId', lastFilters.diagnosticId)
    }
    if (lastFilters.status && lastFilters.status !== '0') {
      params.append('status', lastFilters.status)
    }

    return params.toString() ? `?${params.toString()}` : ''
  } catch (e) {
    console.warn('Failed to parse saved filters:', e)
    return ''
  }
}

const HideExamDiagnosis: React.FC<React.PropsWithChildren<{ subtype?: ExamSubtype | null }>> = ({ subtype, children }) => {
  if (subtype === 'cxray_l_lat' || subtype === 'cxray_r_lat' || subtype === 'xray_other') {
    return null
  }
  return children
}

const FETCH_ERROR_MESSAGES: Record<number, string> = {
  404: 'Análise não encontrada',
  403: 'Você não tem permissão para acessar esta análise',
  500: 'Erro no servidor. Tente novamente mais tarde'
}

const getFetchErrorMessage = (status?: number): string =>
  (status && FETCH_ERROR_MESSAGES[status]) ||
  'Erro ao carregar dados da análise'

const fetchHospitalLogoUrl = async (
  hospitalId: string
): Promise<string | null> => {
  if (!hospitalId) return null
  const establishment = await getEstablishmentById(hospitalId)
  if (!establishment.success || !establishment.data) return null
  return establishment.data.logo_url ?? null
}

export const ExamDetailPage = () => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [diagnosticData, setDiagnosticData] = useState<IExamResponse | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { diagnosticId } = useParams<{ diagnosticId: string }>()
  const toast = useToast()
  // Fica no componente externo: RenderContent é recriado a cada render.
  const xrayOpenedAt = useRef<number | null>(null)

  /**
   * Handle back navigation - uses browser history if available and has more than 1 entry,
   * otherwise navigates to diagnostics list with filters from sessionStorage
   */
  const handleGoBack = useCallback(() => {
    // Check if we have a valid history to go back to
    if (globalThis.history?.length && globalThis.history.length > 1) {
      globalThis.history.back()
    } else {
      // Fallback: navigate to the correct list (Raio-X ou ECG) with its filters
      const type = diagnosticData?.type
      const config =
        type === 'ecg'
          ? ECG_LIST_CONFIG
          : type === 'cxray'
            ? CXRAY_LIST_CONFIG
            : null
      if (config) {
        navigate(`/${config.basePath}${getFiltersUrlParams(config.storageKey)}`)
      } else {
        navigate('/')
      }
    }
  }, [navigate, diagnosticData])

  const handleSubtypeUpdated = useCallback(
    (examId: string, subtype: ExamSubtype) => {
      setDiagnosticData(prev => {
        if (!prev) return prev
        // Main exam re-marked
        if (prev.id_exam === examId) {
          // O backend apaga os órgãos segmentados quando o exame sai de AP/PA.
          return {
            ...prev,
            subtype,
            organ_segmentations: isApPaSubtype(subtype)
              ? prev.organ_segmentations
              : []
          }
        }
        // Related (lateral) exam re-marked
        return {
          ...prev,
          related_exams: prev.related_exams?.map(exam =>
            exam.id_exam === examId ? { ...exam, subtype } : exam
          )
        }
      })
    },
    []
  )

  const updateStatus = async (diagnosisId: string, status: number) => {
    const response = await updateDiagnosisStatus(diagnosisId, status)

    if (response.success) {
      setDiagnosticData(prev => {
        if (!prev) return prev
        return {
          ...prev,
          diagnosis_status: status,
          diagnosis_updated_at: new Date().toISOString()
        }
      })
      toast.handler({
        severity: 'success',
        content: 'Status da análise atualizado com sucesso'
      })
    } else {
      toast.handler({
        severity: 'error',
        content: 'Erro ao atualizar status da análise'
      })
    }
  }

  useEffect(() => {
    const fetchDiagnosticData = async () => {
      if (!diagnosticId) {
        setState('error')
        setError('ID da análise não fornecido')
        return
      }

      setState('loading')
      try {
        const response = await getExamDetail(diagnosticId)
        if (response.success && response.data) {
          setDiagnosticData(response.data)
          if (response.data.type === 'cxray') {
            xrayOpenedAt.current = performance.now()
            analytics.track('xray_page_viewed', { page: 'detail' })
          }
          if (
            !response.data.diagnosis_updated_at &&
            response.data.diagnosis_status === 0
          ) {
            updateStatus(response.data.diagnosis_id, 0)
          }
          setState('success')
        } else {
          setState('error')
          setError(
            response.status === 404
              ? 'Análise não encontrada'
              : 'Erro ao carregar dados da análise'
          )
        }
      } catch (err: any) {
        console.error('Error fetching diagnostic detail:', err)
        setState('error')
        setError(getFetchErrorMessage(err?.response?.status))
      }
    }

    fetchDiagnosticData()
  }, [diagnosticId])

  if (!diagnosticData && state !== 'loading') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant='h5' color='error'>
          {error || 'Análise não encontrada'}
        </Typography>
        <ButtonBase onClick={handleGoBack} sx={{ mt: 2 }}>
          Voltar para listagem
        </ButtonBase>
      </Box>
    )
  }

  const RenderContent = () => {
    const [medicalReport, setMedicalReport] = useState('')
    const [technicalFeedback, setTechnicalFeedback] = useState('')
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
    const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
    const [isSavingFeedback, setIsSavingFeedback] = useState(false)
    const { annotations: ecgAnnotations } = useECGAnnotations()
    const { annotations: xrayAnnotations } = useXRayAnnotations()
    const { generatePDF } = useGeneratePDF()
    const initialTechnicalFeedback = useRef('')
    const { initializeAgreements } = useDiagnosisAgreement()
    const [examReports, setExamReports] = useState<IExamReportResponse[]>([])
    const [examReportStatus, setExamReportStatus] =
      useState<IStateStdTypes>('idle')

    const fetchExamReports = async (diagnosisId: string) => {
      if (examReportStatus === 'loading') return

      setExamReportStatus('loading')
      const response = await getExamReports(diagnosisId)

      if (response.success && response.data) {
        setExamReports(response.data)
        setExamReportStatus('success')
      } else {
        setExamReportStatus('error')
      }
    }

    useEffect(() => {
      if (diagnosticData?.exam_results) {
        initializeAgreements(diagnosticData.exam_results)

        fetchExamReports(diagnosticData.diagnosis_id)
      }
    }, [diagnosticData])

    const handleMedicalReportChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setMedicalReport(e.target.value)
      },
      []
    )

    const handleTechnicalFeedbackChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setTechnicalFeedback(e.target.value)
      },
      []
    )

    const hasFeedbackChanged = useMemo(
      () => technicalFeedback !== initialTechnicalFeedback.current,
      [technicalFeedback]
    )

    const handleOpenFeedbackDialog = useCallback(() => {
      setIsFeedbackDialogOpen(true)
    }, [])

    const handleCloseFeedbackDialog = useCallback(() => {
      setIsFeedbackDialogOpen(false)
    }, [])

    const examDetail: IExamResponse = useMemo(
      () => ({
        ...diagnosticData!,
        medicalReport: medicalReport,
        technicalFeedback: technicalFeedback
      }),
      [diagnosticData, medicalReport, technicalFeedback]
    )

    const renderDiagnosis = useMemo(
      () => ({
        ecg: <ECGDiagnostics examData={examDetail} />,
        cxray: <XRayDiagnostics examData={examDetail} />
      }),
      [examDetail]
    )

    const renderExam = useMemo(
      () => ({
        ecg: <ECGExam exameData={examDetail} />,
        cxray: (
          <XRayExam
            examData={examDetail}
            onSubtypeUpdated={handleSubtypeUpdated}
          />
        )
      }),
      [examDetail]
    )

    const renderAnnotations = useMemo(
      () => ({
        ecg: <ECGAnnotationsSummary />,
        cxray: <XRayAnnotationsSummary />
      }),
      []
    )

    const handleGenerateReport = useCallback(async () => {
      setIsGeneratingPDF(true)
      let reportSent = false
      try {
        // Logotipo do hospital dono do exame, resolvido ANTES do render abaixo:
        // o html2canvas captura ~100ms depois, então uma busca assíncrona
        // dentro do relatório não chegaria a tempo. Falha aqui não impede a
        // geração — o cabeçalho apenas sai sem logotipo.
        const logoUrl = await fetchHospitalLogoUrl(examDetail.id_hospital)

        // Criar elemento temporário para PDF
        const printElement = document.createElement('div')
        printElement.id = 'printable-diagnostic-report'
        printElement.style.position = 'absolute'
        printElement.style.left = '-9999px'
        printElement.style.top = '0'
        printElement.style.width = '210mm'
        printElement.style.backgroundColor = 'white'
        document.body.appendChild(printElement)

        // Renderizar conteúdo usando React
        const { createRoot } = await import('react-dom/client')
        const root = createRoot(printElement)

        // A captura espera o relatório sinalizar prontidão, em vez de um atraso
        // fixo: no raio-X, 100ms não davam tempo de baixar e comprimir a imagem
        // original, e o PDF saía com "Carregando..." no lugar dela. O timeout
        // fica só como rede de segurança para o sinal nunca chegar.
        const PRINT_READY_TIMEOUT_MS = 15000

        await new Promise<void>(resolve => {
          let settled = false

          const settle = () => {
            if (settled) return
            settled = true
            // Dois frames para o layout assentar antes de medir as seções.
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
          }

          const safetyTimeout = setTimeout(settle, PRINT_READY_TIMEOUT_MS)

          const handleReady = () => {
            clearTimeout(safetyTimeout)
            settle()
          }

          root.render(
            <PrintableReport
              diagnosticDetail={examDetail}
              ecgAnnotations={ecgAnnotations}
              xrayAnnotations={xrayAnnotations}
              medicalReport={medicalReport}
              logoUrl={logoUrl}
              onReady={handleReady}
            />
          )
        })

        const filename = `revisao_${examDetail.type}_${examDetail.patient_id}_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`
        const result = await generatePDF(
          'printable-diagnostic-report',
          filename
        )

        // Limpar
        root.unmount()
        printElement.remove()

        if (result.success && result.blob) {
          // Enviar PDF para o backend usando FormData
          const uid = Storage.user.get()?.professional_id || ''
          const uploadResponse = await uploadReviewPDF(
            result.blob,
            filename,
            examDetail.diagnosis_id,
            uid
          )

          fetchExamReports(examDetail.diagnosis_id)

          if (uploadResponse.success) {
            reportSent = true
            toast.handler({
              severity: 'success',
              content: 'Revisão gerada e enviada com sucesso'
            })

            // Atualizar status para "Revisado" (1)
            if (examDetail.diagnosis_status !== 1) {
              await updateStatus(examDetail.diagnosis_id, 1)
            }
          } else {
            toast.handler({
              severity: 'error',
              content: 'Erro ao enviar revisão para o servidor'
            })
          }
        } else {
          toast.handler({
            severity: 'error',
            content: 'Erro ao gerar PDF'
          })
        }
      } catch (error) {
        console.error('Error generating/uploading report:', error)
        toast.handler({
          severity: 'error',
          content: 'Erro ao gerar ou enviar revisão'
        })
      } finally {
        setIsGeneratingPDF(false)
        if (examDetail.type === 'cxray' && xrayOpenedAt.current !== null) {
          analytics.track('xray_report_generated', {
            success: reportSent,
            duration_since_open_ms: Math.round(
              performance.now() - xrayOpenedAt.current
            )
          })
        }
      }
    }, [
      examDetail,
      ecgAnnotations,
      xrayAnnotations,
      generatePDF,
      medicalReport,
      technicalFeedback,
      toast
    ])

    const handleSendFeedback = useCallback(async () => {
      setIsSavingFeedback(true)
      const uid = Storage.user.get()?.professional_id || ''
      const data: IFeedbackPayload = {
        id_exam_diagnosis: examDetail.diagnosis_id,
        id_professional: uid,
        content: technicalFeedback
      }
      const response = await createFeedback(data)

      if (!response.success) {
        toast.handler({
          severity: 'error',
          content: 'Erro ao enviar feedback técnico'
        })
      } else {
        toast.handler({
          severity: 'success',
          content: 'Feedback técnico enviado com sucesso'
        })
        initialTechnicalFeedback.current = technicalFeedback
        setIsFeedbackDialogOpen(false)
        if (examDetail.type === 'cxray') {
          analytics.track('xray_technical_feedback_sent', {})
        }
      }

      setIsSavingFeedback(false)
    }, [technicalFeedback, examDetail.diagnosis_id, examDetail.type])

    return (
      <Box sx={{ padding: 2, width: '100%', maxWidth: 1310, margin: '0 auto' }}>
        {/* Header */}
        <Grid
          container
          spacing={2}
          alignItems='center'
          mb={3}
          sx={{
            overflow: { xs: 'hidden', md: 'visible' },
            wordBreak: { xs: 'break-all', md: 'normal' }
          }}
        >
          <Grid size={12} paddingBlock={2}>
            <Box
              onClick={handleGoBack}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: 'fit-content',
                cursor: 'pointer',
                color: 'primarySoft.main',
                '&:hover': {
                  opacity: 0.8
                }
              }}
              role='button'
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleGoBack()
                }
              }}
            >
              <ArrowBack />
              Lista de Análises
            </Box>
          </Grid>
          <Grid
            size={12}
            container
            spacing={1}
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography variant='h5'>
              Revisão de {EXAM_TYPE_MAP[examDetail.type]}
            </Typography>
            {/* Technical Feedback Button */}
            <Grid container mb={3}>
              <Grid size={12}>
                <ButtonBase
                  onClick={handleOpenFeedbackDialog}
                  sx={{ px: 4 }}
                  variant='outlined'
                >
                  <FeedbackOutlined sx={{ mr: 1 }} />
                  {technicalFeedback ? 'Editar' : 'Adicionar'} comentário
                  técnico
                  {hasFeedbackChanged && ' *'}
                </ButtonBase>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12} container spacing={1} direction='column'>
            <Typography variant='body2'>
              Data da análise:{' '}
              <b>
                {dayjs(examDetail.diagnosis_created_at)
                  .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
                  .format('DD/MM/YYYY HH:mm:ss')}
              </b>
            </Typography>
            <Typography variant='body2'>
              Identificador do paciente: <b>{examDetail.patient_id}</b>
            </Typography>
            <Typography variant='body2'>
              Identificador da análise: <b>{examDetail.id_exam}</b>
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems='center' mb={3}></Grid>

        {/* Exam Visualization Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ backgroundColor: 'background.paper' }}>
            <Grid container spacing={2}>
              <Grid size={12} sx={{ overflow: { xs: 'auto', md: 'hidden' } }}>
                {renderExam[examDetail.type]}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <HideExamDiagnosis subtype={examDetail.subtype}>
          {/* Diagnosis Section */}
          {renderDiagnosis[examDetail.type]}

          {/* Anotações Médicas */}
          {renderAnnotations[examDetail.type]}

          {/* Medical Report Section */}
          <Grid container mb={3}>
            <Grid size={12}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Revisão médica por extenso:
              </Typography>
              <TranscriptionInput
                value={medicalReport}
                onChange={handleMedicalReportChange}
                placeholder='Digite a revisão médica por extenso...'
                rows={4}
              />
            </Grid>
          </Grid>

          {/* Feedback Dialog */}
          <FeedbackDialog
            open={isFeedbackDialogOpen}
            onClose={handleCloseFeedbackDialog}
            value={technicalFeedback}
            onChange={handleTechnicalFeedbackChange}
            onSave={handleSendFeedback}
            hasChanged={hasFeedbackChanged}
            isSaving={isSavingFeedback}
          />

          {/* Generate Report Button */}
          <Grid container justifyContent='flex-end' spacing={2}>
            <Grid size='auto'>
              <ButtonBase
                onClick={handleGenerateReport}
                sx={{ px: 4 }}
                disabled={isGeneratingPDF}
              >
                {isGeneratingPDF ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Gerando PDF...
                  </>
                ) : (
                  <>
                    <PictureAsPdf sx={{ mr: 1 }} />
                    Gerar revisão
                  </>
                )}
              </ButtonBase>
            </Grid>
          </Grid>

          <AccordionBase
            sx={{ mt: 2 }}
            defaultExpanded={false}
            title='Revisões médicas enviadas'
          >
            {examReportStatus === 'loading' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : examReportStatus === 'success' ? (
              <Grid container spacing={2}>
                {examReports.length == 0 ? (
                  <Grid size={12}>
                    <Typography variant='body2'>
                      Nenhuma revisão médica encontrada.
                    </Typography>
                  </Grid>
                ) : (
                  examReports.map((ref, index: number) => (
                    <Grid
                      key={index}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 3
                      }}
                    >
                      <Reference
                        link={ref.link_report}
                        title={ref.filename ?? `Revisão ${index + 1}`}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
            ) : (
              <Box />
            )}
          </AccordionBase>
        </HideExamDiagnosis>
      </Box>
    )
  }

  const render: Record<IStateStdTypes, JSX.Element> = {
    idle: (
      <Grid size={12}>
        <Typography variant='body2'>
          Aguardando seleção de análise...
        </Typography>
      </Grid>
    ),
    loading: <DiagnosticDetailSkeleton />,
    success: (
      <DiagnosisAgreementProvider>
        <ECGAnnotationsProvider>
          <XRayAnnotationsProvider>
            <OrganSelectionProvider>
              <RenderContent />
            </OrganSelectionProvider>
          </XRayAnnotationsProvider>
        </ECGAnnotationsProvider>
      </DiagnosisAgreementProvider>
    ),
    error: (
      <Grid size={12}>
        <Typography variant='body2' color='error'>
          {error || 'Erro ao carregar dados.'}
        </Typography>
        <ButtonBase onClick={handleGoBack} sx={{ mt: 2 }}>
          Voltar para listagem
        </ButtonBase>
      </Grid>
    )
  }

  return render[state]
}

export default ExamDetailPage
